import React, { useEffect, useRef, useState } from "react";

// Sticky, horizontally scrollable in-page nav that highlights the section in view.
function SectionNav({ sections }) {
  const [active, setActive] = useState(sections[0].id);
  const listRef = useRef(null);
  const linkRefs = useRef({});

  // The current section is the last one whose top has passed 35% of the
  // viewport; above the first section, the first one stays active.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight * 0.35;
      let current = sections[0].id;
      for (const { id } of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [sections]);

  // Keep the active pill visible on narrow screens without scrolling the page.
  useEffect(() => {
    const list = listRef.current;
    const link = linkRefs.current[active];
    if (!list || !link) return;
    const target = link.offsetLeft - list.clientWidth / 2 + link.clientWidth / 2;
    list.scrollTo({ left: target, behavior: "smooth" });
  }, [active]);

  const handleClick = (e, id) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActive(id);
  };

  return (
    <nav
      aria-label="On this page"
      className="sticky top-[68px] md:top-[84px] z-30 border-b border-gray-200 bg-white/95 backdrop-blur"
    >
      <ul
        ref={listRef}
        className="max-w-7xl mx-auto flex gap-1 sm:gap-2 overflow-x-auto px-4 sm:px-6 lg:px-8 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sections.map(({ id, label }) => (
          <li key={id} className="shrink-0">
            <a
              ref={(el) => (linkRefs.current[id] = el)}
              href={`#${id}`}
              onClick={(e) => handleClick(e, id)}
              aria-current={active === id ? "location" : undefined}
              className={`block rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                active === id
                  ? "bg-brand-navy text-white"
                  : "text-gray-600 hover:bg-brand-yellow/30 hover:text-brand-navy"
              }`}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default SectionNav;
